import {
  Menu,
  LogOut,
  ImageMinus,
  Shirt,
  ImageUpscale,
  Fullscreen,
  CircleUserRound,
  CircleHelp,
  AlignLeft,
  X,
  Bell,
  Settings,
  HistoryIcon,
  CreditCardIcon,
  Layers,
  List
} from "lucide-react";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from '../AuthProvider';
import { useAccessControl } from "../AccessControlProvider";
import { useMobileMenu } from "../MobileMenuContext";
import logo from "../assets/logo.png"

const SIDEBAR_ITEMS = [
  {
    id: "ai_model",
    name: "AI Model",
    icon: Shirt,
    color: "#6366f1",
    href: "/ai-model",
  },
  {
    id: "batch_processing",
    name: "Batch Status",
    icon: List,
    color: "#6366f1",
    href: "/batch-status",
  },
  {
    id: "background_remover",
    name: "Background Remover",
    icon: ImageMinus,
    color: "#6366f1",
    href: "/background-remover",
  },
  {
    id: "image_upscale",
    name: "Image Upscaler",
    icon: ImageUpscale,
    color: "#6366f1",
    href: "/image-up-scale",
  },
  {
    id: "image_outpaint",
    name: "Image Expander",
    icon: Fullscreen,
    color: "#6366f1",
    href: "/image-expand",
  }
];

const SETTINGS_ITEMS = [
  { 
    name: "Recent Activities", 
    icon: HistoryIcon, 
    color: "#6366f1", 
    href: "/recentactivities" 
  },
  {
   name: "Billing & Plans", 
    icon: CreditCardIcon, 
    color: "#6366f1", 
    href: "/billing" 
  },
  { 
    name: "Help & Support", 
    icon: CircleHelp, 
    color: "#6366f1", 
    href: "/help" 
  }
];

export const Sidebar = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { privileges, loading: privilegesLoading } = useAccessControl();
  const { setIsMobileMenuOpen } = useMobileMenu();
  
  // Sync sidebar state with mobile menu context
  useEffect(() => {
    if (isMobile) {
      setIsMobileMenuOpen(isSidebarOpen);
    }
  }, [isSidebarOpen, isMobile, setIsMobileMenuOpen]);
  

  let features = privilegesLoading ? [] : (privileges.privileges?.features || []);
  // Filter sidebar items based on user privileges only after loading
  const filteredSidebarItems = !privilegesLoading ? SIDEBAR_ITEMS.filter(item => 
    features.includes(item.id)
  ) : [];

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      
      if (mobile) {
        setIsSidebarOpen(false);
      } else {
        setIsSidebarOpen(true);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      navigate('/');
      setTimeout(async () => {
        try {
          await logout();
        } catch (error) {
          console.error("Logout failed:", error);
        }
      }, 100);
    } catch (error) {
      console.error("Navigation failed:", error);
    }
  };

  const handleLinkClick = () => {
    if (isMobile) {
      setIsSidebarOpen(false);
    }
  };

  if (privilegesLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile Hamburger Button */}
      {isMobile && !isSidebarOpen && (
        <motion.button
          className="fixed top-5 left-4 z-50 "
          onClick={() => setIsSidebarOpen(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <AlignLeft size={28} className="text-black" />
        </motion.button>
      )}

      {/* Mobile Overlay */}
      <AnimatePresence>
        {isMobile && isSidebarOpen && (
          <motion.div
            className="fixed inset-0 bg-transparent bg-opacity-50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <AnimatePresence>
        {(isSidebarOpen || !isMobile) && (
          <motion.div
            className={`${
              isMobile 
                ? "fixed left-0 top-0 h-full z-50 w-full" 
                : "relative flex-shrink-0"
            } ${
              isSidebarOpen ? "w-68" : "w-20"
            } transition-all duration-300 ease-in-out`}
            initial={isMobile ? { x: -256 } : false}
            animate={isMobile ? { x: 0 } : { width: isSidebarOpen ? 256 : 80 }}
            exit={isMobile ? { x: -256 } : false}
            transition={{ duration: 0.3, ease: "easeInOut" }}
          >
            <div className="h-full bg-white text-gray-800 border-r border-[#F8FAFC] p-4 flex flex-col ">
              {/* Header with Logo and Close Button */}
              <div className="flex items-center justify-between mb-6">
                <Link to="/" onClick={handleLinkClick}>
                  <motion.div 
                    className="flex items-center h-16 cursor-pointer hover:shadow-lg transition-all"
                    whileHover={{ scale: 1.02 }}
                  >
                    <img src={logo} alt="" className="w-20 h-20"/> 
                  </motion.div>
                </Link>
                
                {/* Close/Collapse Button */}
                {isSidebarOpen && (
                  <motion.button 
                  
                    onClick={() => setIsSidebarOpen(false)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                  >
                    {isMobile && (
                      <X size={20} className="text-gray-600" />
                    )}
                  </motion.button>
                )}
              </div>

              {/* Main Navigation */}
              <nav className="flex-grow">
                {filteredSidebarItems.map((item) => {
                  const isActive = location.pathname === item.href;

                  return (
                    <Link key={item.href} to={item.href} onClick={handleLinkClick}>
                      <motion.div
                        className={`flex items-center p-3 text-sm font-medium rounded-lg transition-all mb-2 ${
                          isActive 
                            ? "bg-[#ECF0FF] text-[#4B5565] shadow-sm border border-indigo-200"
                            : "hover:bg-indigo-50 text-gray-700"
                        }`}
                        whileHover={{ scale: 1.02 }}
                      >
                        <item.icon
                          size={18}
                          className={isActive ? "text-[#4F39F6]" : "text-[#4B5565]"}
                        />
                        <AnimatePresence>
                          {isSidebarOpen && (
                            <motion.span
                              className="ml-3 whitespace-nowrap text-[16px]"
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              transition={{ duration: 0.2 }}
                            >
                              {item.name}
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    </Link>
                  );
                })}
              </nav>

              {/* Bottom Section - Settings & Profile */}
              <div className="mt-auto">
                {/* Settings & Support Section */}
                <div className="pb-4">
                  {isSidebarOpen && (
                    <h3 className="text-md md:text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                      Settings & Support
                    </h3>
                  )}
                  
                  {SETTINGS_ITEMS.map((item) => {
                    const isActive = location.pathname === item.href;

                    return (
                      <Link key={item.href} to={item.href} onClick={handleLinkClick}>
                        <motion.div
                          className={`flex items-center justify-between p-3 text-sm font-medium rounded-lg transition-all mb-2 ${
                            isActive 
                              ? "bg-[#ECF0FF] text-[#4B5565] shadow-sm border border-indigo-200"
                              : "hover:bg-indigo-50 text-gray-700"
                          }`}
                          whileHover={{ scale: 1.02 }}
                        >
                          <div className="flex items-center">
                            <item.icon
                              size={24}
                              className={isActive ? "text-[#4F39F6]" : "text-[#4B5565]"}
                            />
                            <AnimatePresence>
                              {isSidebarOpen && (
                                <motion.span
                                  className="ml-3 whitespace-nowrap text-[16px]"
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  exit={{ opacity: 0, x: -10 }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {item.name}
                                </motion.span>
                              )}
                            </AnimatePresence>
                          </div>
                        </motion.div>
                      </Link>
                    );
                  })}
                </div>

                {/* User Profile Section */}
                {currentUser && (
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex items-center p-3">
                      <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {/* <img
                          src={
                            currentUser?.photoURL ||
                            "https://images.unsplash.com/photo-1633332755192-727a05c4013d?q=80&w=2080&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                          }
                          alt="Profile"
                          className="w-full h-full object-cover"
                        /> */}
                      </div>
                      
                      <AnimatePresence>
                        {isSidebarOpen && (
                          <motion.div
                            className="ml-3 flex-1 min-w-0"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {currentUser?.displayName || "User"}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              {currentUser?.email || "user@example.com"}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <AnimatePresence>
                        {isSidebarOpen && (
                          <motion.button
                            className="cursor-pointer ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            onClick={(e) => {
                              handleLogout(e);
                              if (isMobile) setIsSidebarOpen(false);
                            }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            title="Logout"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                          >
                            <LogOut size={16} />
                          </motion.button>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Menu button to expand sidebar when collapsed */}
      {!isMobile && !isSidebarOpen && (
        <motion.div
          className="absolute top-4 right-0 translate-x-1/2 bg-white p-2 rounded-full shadow-md cursor-pointer z-20"
          onClick={() => setIsSidebarOpen(true)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Menu size={20} className="text-indigo-600" />
        </motion.div>
      )}
    </>
  );
};